import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDataStore } from '../store/dataStore';
import { fetchSnapshot, fetchHistoricalIndex, cacheSnapshot, getCachedSnapshot } from '../services/api';
import type { DataSnapshot, HistoricalIndex } from '@bedrock-profiles/shared';

interface HistoricalDataResult {
  currentSnapshot?: DataSnapshot;
  historicalIndex?: HistoricalIndex;
  selectedVersion: string;
  isLoading: boolean;
  error?: string;
  retry?: () => Promise<unknown>;
  isHistoryLoading: boolean;
  historyError?: string;
}

/**
 * Hook to load and manage historical data using TanStack Query
 * for automatic caching, deduping, and background refetching
 */
export function useHistoricalData(): HistoricalDataResult {
  const { selectedVersion, setCurrentSnapshot, setHistoricalIndex } = useDataStore();

  // Query for historical index (pure function, no side effects)
  const {
    data: historicalIndex,
    isLoading: isHistoryIndexLoading,
    error: historyIndexError,
  } = useQuery({
    queryKey: ['historicalIndex'],
    queryFn: fetchHistoricalIndex,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    refetchOnMount: false,
  });

  // Query for snapshot data (pure function, no side effects)
  const {
    data: currentSnapshot,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['snapshot', selectedVersion],
    queryFn: async () => {
      // Try to get from cache first (skip for latest to avoid stale data)
      if (selectedVersion !== 'latest') {
        const cached = await getCachedSnapshot(selectedVersion);
        if (cached) {
          return cached;
        }
      }

      // Fetch from network
      const snapshotEntry = historicalIndex?.snapshots.find((entry) => entry.date === selectedVersion);
      const snapshot = await fetchSnapshot(selectedVersion, snapshotEntry?.snapshotUrl);

      // Cache for offline use (non-latest only)
      if (selectedVersion !== 'latest') {
        await cacheSnapshot(selectedVersion, snapshot);
      }

      return snapshot;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    refetchOnMount: false,
    enabled: selectedVersion === 'latest' || !isHistoryIndexLoading,
  });

  // Sync fetched data to Zustand store via useEffect (avoid side effects in queryFn)
  useEffect(() => {
    if (historicalIndex) {
      setHistoricalIndex(historicalIndex);
    }
  }, [historicalIndex, setHistoricalIndex]);

  useEffect(() => {
    if (currentSnapshot) {
      setCurrentSnapshot(currentSnapshot);
    }
  }, [currentSnapshot, setCurrentSnapshot]);

  return {
    currentSnapshot,
    historicalIndex,
    selectedVersion,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to load data') : undefined,
    retry: refetch,
    isHistoryLoading: isHistoryIndexLoading,
    historyError: historyIndexError
      ? historyIndexError instanceof Error
        ? historyIndexError.message
        : 'Failed to load historical data index'
      : undefined,
  };
}
